// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Bot, Wrench, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import type { SupportAgentAction } from '@/types/entities'

interface AgentActionsPanelProps {
  actions: SupportAgentAction[]
  isLoading?: boolean
}

export function AgentActionsPanel({ actions, isLoading }: AgentActionsPanelProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  if (actions.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm">
        No agent actions recorded for this ticket.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Bot className="h-4 w-4 text-violet-500" />
        <h3 className="text-sm font-semibold">Agent Audit Trail</h3>
        <Badge variant="secondary" className="text-xs">{actions.length} steps</Badge>
      </div>

      <div className="space-y-2">
        {actions.map((action, index) => (
          <ActionCard key={action.id} action={action} stepNumber={index + 1} />
        ))}
      </div>
    </div>
  )
}

function ActionCard({ action, stepNumber }: { action: SupportAgentAction; stepNumber: number }) {
  const actionLabels: Record<string, string> = {
    classify: 'Classification',
    tool_call: 'Tool Call',
    draft: 'Draft Response',
    draft_stored: 'Draft Stored',
    send: 'Reply Sent',
    escalate: 'Escalated',
  }

  const actionIcons: Record<string, React.ReactNode> = {
    classify: <Bot className="h-3.5 w-3.5" />,
    tool_call: <Wrench className="h-3.5 w-3.5" />,
    draft: <Bot className="h-3.5 w-3.5" />,
    draft_stored: <Clock className="h-3.5 w-3.5" />,
    send: <CheckCircle className="h-3.5 w-3.5" />,
    escalate: <AlertTriangle className="h-3.5 w-3.5" />,
  }

  return (
    <Card className="border-border/60">
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center h-5 w-5 rounded-full bg-violet-100 text-violet-600 dark:bg-violet-900 dark:text-violet-300 text-xs font-bold">
              {stepNumber}
            </span>
            <span className="text-violet-600 dark:text-violet-400">
              {actionIcons[action.actionType] || <Bot className="h-3.5 w-3.5" />}
            </span>
            <span className="text-sm font-medium">
              {actionLabels[action.actionType] || action.actionType}
            </span>
            {action.toolName && (
              <Badge variant="outline" className="text-xs font-mono">
                {action.toolName}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {action.success ? (
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-red-500" />
            )}
            {action.latencyMs !== null && (
              <span className="text-xs text-muted-foreground font-mono">
                {action.latencyMs}ms
              </span>
            )}
          </div>
        </div>

        {/* Model info */}
        {action.modelUsed && (
          <div className="mt-1.5 text-xs text-muted-foreground">
            Model: <span className="font-mono">{action.modelUsed}</span>
          </div>
        )}

        {/* Tool input/output preview */}
        {action.toolInput && (
          <details className="mt-2">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
              Input
            </summary>
            <pre className="mt-1 text-xs bg-muted/50 rounded p-2 overflow-x-auto max-h-32">
              {JSON.stringify(action.toolInput, null, 2)}
            </pre>
          </details>
        )}
        {action.toolOutput && (
          <details className="mt-1">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
              Output
            </summary>
            <pre className="mt-1 text-xs bg-muted/50 rounded p-2 overflow-x-auto max-h-32">
              {JSON.stringify(action.toolOutput, null, 2)}
            </pre>
          </details>
        )}

        {/* Error */}
        {action.errorMessage && (
          <div className="mt-2 text-xs text-red-600 bg-red-50 dark:bg-red-950/30 rounded p-2">
            {action.errorMessage}
          </div>
        )}

        {/* Timestamp */}
        <div className="mt-1.5 text-xs text-muted-foreground">
          {formatDateTime(action.createdAt)}
        </div>
      </CardContent>
    </Card>
  )
}
